import { Autocomplete, AutocompleteProps, PaperProps, SvgIcon } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDown from '../../../../icons/svg/arrow_down.svg?react';
import { RoundedPaper } from '../../surface/roundedPaper';
import { SelectOption } from '../select';
import { TextField, TextFieldProps } from '../textField';

type Props = {
  onChange: (option: string | number | undefined) => void;
  value?: string | number | null;
  defaultValue?: string | number;
  missing?: boolean;
  missingMessage?: string;
  startIcon?: JSX.Element;
} & Pick<
  TextFieldProps,
  'label' | 'error' | 'helperText' | 'name' | 'required' | 'disabled' | 'id' | 'placeholder'
> &
  Pick<
    AutocompleteProps<SelectOption, undefined, boolean, undefined>,
    'options' | 'onBlur' | 'disableClearable'
  >;
export type SearchableSelectProps = Props;

const CustomPaperComponent = (props: PaperProps) => (
  <RoundedPaper {...props} className="shadow-500 w-[350px]" />
);

export const SearchableSelect: FC<Props> = ({
  options,
  onChange,
  value,
  defaultValue,
  label,
  error,
  helperText,
  name,
  required,
  disabled,
  disableClearable,
  id,
  missing,
  missingMessage,
  startIcon
}) => {
  const { t } = useTranslation(['template', 'common']);

  const [inputValue, setInputValue] = useState<string | undefined>(
    value ? options.find((option) => option.value === value)?.text : ''
  );

  useEffect(() => {
    setInputValue(value ? options.find((option) => option.value === value)?.text : '');
  }, [value, options]);

  const handleFocus = () => {
    setInputValue('');
  };

  const handleBlur = () => {
    if (!inputValue) {
      setInputValue(options.find((option) => option.value === value)?.text);
    }
  };

  const { valueOption, defaultValueOption } = useMemo(() => {
    const valueOption = options.find((option) => option.value === value) || null;
    const defaultValueOption = options.find((option) => option.value === defaultValue);

    return {
      valueOption,
      defaultValueOption
    };
  }, [options, value, defaultValue]);

  return (
    <Autocomplete
      options={options}
      sx={{
        '& .MuiInput-root': {
          paddingBottom: '12px !important'
        },
        '& .MuiAutocomplete-popupIndicator': {
          paddingX: 0,
          marginRight: '12px'
        },
        '& .MuiAutocomplete-clearIndicator': {
          marginRight: '4px',
          '& > svg': {
            fontSize: '1rem'
          }
        }
      }}
      PaperComponent={CustomPaperComponent}
      noOptionsText={t('template:NO_MATCHING_OPTION')}
      openText={t('common:OPEN')}
      getOptionLabel={(option) => option.text}
      value={valueOption}
      disabled={disabled}
      disableClearable={disableClearable}
      defaultValue={defaultValueOption}
      onChange={(_, selectedOption) => {
        setInputValue(selectedOption?.text);
        onChange(String(selectedOption?.value));
      }}
      popupIcon={<SvgIcon component={ArrowDown} color="primary" />}
      onFocus={handleFocus}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          name={name}
          id={id}
          startAdornment={startIcon ?? params.InputProps.startAdornment}
          endAdornment={params.InputProps.endAdornment}
          fullWidth={params.fullWidth}
          disabled={params.disabled}
          inputProps={{
            ...params.inputProps,
            value: inputValue ?? ''
          }}
          onChange={(e) => setInputValue(e.target.value)}
          inputRef={params.InputProps.ref}
          label={label}
          error={error}
          helperText={helperText}
          placeholder={t('common:SEARCH')}
          required={required}
          missing={missing}
          missingMessage={missingMessage}
        />
      )}
    />
  );
};
